import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  ClipboardList,
  LogOut,
  User as UserIcon,
  X,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { MobilisationSwitcher } from './MobilisationSwitcher';
import { buildNavSections } from './navConfig';
import { NavBadge } from './NavBadge';
import { useCurrentScope } from '../../hooks/useMobilisations';
import type { SessionUser } from '../../hooks/useAuth';

interface MobileNavDrawerProps {
  user: SessionUser;
  open: boolean;
  onClose: () => void;
  onLogout: () => void;
}

/**
 * Right-anchored slide-in panel for screens narrower than `lg`. Mirrors
 * the desktop Sidebar's visual language (gradient brand mark, accent-
 * tinted active fill) so muscle memory carries across form factors.
 * Closes on backdrop click, Escape, or after a link is followed.
 */
export function MobileNavDrawer({
  user,
  open,
  onClose,
  onLogout,
}: MobileNavDrawerProps) {
  // Called before the `!open` early return so the hook order stays stable.
  const scope = useCurrentScope();

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const sections = buildNavSections(user, {
    dayToDay: scope?.kind === 'onboarding',
  });

  const linkClass = (isActive: boolean) =>
    [
      'flex items-center gap-3 rounded-token-sm px-3 py-2 text-sm font-medium',
      'min-h-touch transition-colors',
      isActive
        ? 'bg-accent/10 text-accent'
        : 'text-fg-muted hover:bg-surface-2 hover:text-fg',
    ].join(' ');

  return (
    <div
      className="fixed inset-0 z-50 lg:hidden print:hidden"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close menu"
        onClick={onClose}
        className="animate-fade-in absolute inset-0 bg-fg/40 backdrop-blur-sm"
      />
      <aside className="animate-slide-in-right absolute right-0 top-0 flex h-full w-80 max-w-[85vw] flex-col bg-surface shadow-token-lg">
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-line/10 px-3">
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-token-sm text-fg-on-accent shadow-token-accent ring-1 ring-accent/30"
              style={{
                backgroundImage:
                  'linear-gradient(135deg, rgb(var(--accent)) 0%, #ec4899 100%)',
              }}
            >
              <ClipboardList className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <div
                className="truncate text-sm font-semibold tracking-tight text-fg"
                title={user.email}
              >
                {user.display_name}
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<X className="h-4 w-4" />}
            onClick={onClose}
            aria-label="Close menu"
          >
            Close
          </Button>
        </div>

        <div className="border-b border-line/10 px-3 py-3">
          <MobilisationSwitcher />
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {sections.map((entry, i) => {
            if (entry.kind === 'leaf') {
              const Icon = entry.icon;
              return (
                <NavLink
                  key={i}
                  to={entry.to}
                  end={entry.to === '/' || !!entry.exact}
                  onClick={onClose}
                  className={({ isActive }) => `mb-0.5 ${linkClass(isActive)}`}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="truncate">{entry.label}</span>
                  {entry.badge ? <NavBadge kind={entry.badge} /> : null}
                </NavLink>
              );
            }
            return (
              <div key={i} className="mt-3 first:mt-0">
                <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-fg-dim">
                  {entry.label}
                </div>
                <div className="flex flex-col gap-0.5">
                  {entry.items.map((leaf) => {
                    const Icon = leaf.icon;
                    return (
                      <NavLink
                        key={leaf.to}
                        to={leaf.to}
                        end={leaf.to === '/' || !!leaf.exact}
                        onClick={onClose}
                        className={({ isActive }) => linkClass(isActive)}
                      >
                        <Icon className="h-4 w-4 shrink-0" aria-hidden />
                        <span className="truncate">{leaf.label}</span>
                        {leaf.badge ? <NavBadge kind={leaf.badge} /> : null}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {user.person_id ? (
            <div className="mt-3">
              <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-fg-dim">
                You
              </div>
              <NavLink
                to="/me"
                onClick={onClose}
                className={({ isActive }) => linkClass(isActive)}
              >
                <UserIcon className="h-4 w-4 shrink-0" aria-hidden />
                <span className="truncate">My record</span>
              </NavLink>
            </div>
          ) : null}
        </nav>

        <div className="border-t border-line/10 px-3 py-3">
          <Button
            variant="secondary"
            size="md"
            fullWidth
            iconLeft={<LogOut className="h-4 w-4" />}
            onClick={() => {
              onLogout();
              onClose();
            }}
          >
            Sign out
          </Button>
        </div>
      </aside>
    </div>
  );
}
